import React from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { Link } from "react-router-dom";
import { keyframes } from "@emotion/react";

import {
  Breadcrumbs as MuiBreadcrumbs,
  Grid,
  Divider as MuiDivider,
  Typography as MuiTypography,
  Button as MuiButton,
  Box,
} from "@mui/material";
import { spacing } from "@mui/system";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import { green, red } from "@mui/material/colors";

import Actions from "./Actions";
// import BarChart from "./BarChart";
// import LineChart from "./LineChart";
// import DoughnutChart from "./DoughnutChart";
import SpaceAppCards from "./SpaceAppCards";
// import Table from "./Table";
import { hasPermission } from "../../../utils/permissionUtils";

const Divider = styled(MuiDivider)(spacing);

const Typography = styled(MuiTypography)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Button = styled(MuiButton)(spacing);

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StaggeredGrid = styled(Grid)`
  & > .MuiGrid-item:nth-of-type(1) {
    animation: ${fadeInUp} 0.6s ease-out;
  }
  & > .MuiGrid-item:nth-of-type(2) {
    animation: ${fadeInUp} 0.6s ease-out 0.1s both;
  }
  & > .MuiGrid-item:nth-of-type(3) {
    animation: ${fadeInUp} 0.6s ease-out 0.2s both;
  }
`;

const HeaderBox = styled(Box)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 40px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    animation: rotate 20s linear infinite;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

function SpaceApps() {
  const { t } = useTranslation();

  // Debug: Log permissions
  console.log("=== DASHBOARD PERMISSIONS DEBUG ===");
  console.log(
    "rbacPermissions from localStorage:",
    localStorage.getItem("rbacPermissions")
  );
  console.log(
    "Parsed permissions:",
    JSON.parse(localStorage.getItem("rbacPermissions") || "[]")
  );

  // Check if user is client (which bypasses all permissions)
  const accessToken =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");
  if (accessToken) {
    try {
      const base64Url = accessToken.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      const decoded = JSON.parse(jsonPayload);
      console.log("JWT Decoded:", decoded);
      console.log(
        "Has clientId?",
        !!decoded.clientId,
        "| Has userId?",
        !!decoded.userId
      );
      console.log("Is Client (bypasses permissions)?", !!decoded.clientId);
    } catch (e) {
      console.log("Could not decode JWT:", e);
    }
  }

  // Check permissions for each app
  // For Widget: Check WIDGET parent permission or VIEW_WIDGET_DASHBOARD child
  const hasWidgetAccess =
    hasPermission("WIDGET") || hasPermission("VIEW_WIDGET_DASHBOARD");
  console.log(
    "hasWidgetAccess:",
    hasWidgetAccess,
    "| WIDGET:",
    hasPermission("WIDGET"),
    "| VIEW_WIDGET_DASHBOARD:",
    hasPermission("VIEW_WIDGET_DASHBOARD")
  );

  // For Calendar: Check CALENDAR parent permission (NOT CALENDARS which is for settings integrations)
  const hasCalendarAccess =
    hasPermission("CALENDAR") || hasPermission("VIEW_CALENDAR");
  console.log(
    "hasCalendarAccess:",
    hasCalendarAccess,
    "| CALENDAR:",
    hasPermission("CALENDAR"),
    "| VIEW_CALENDAR:",
    hasPermission("VIEW_CALENDAR"),
    "| CALENDARS (settings):",
    hasPermission("CALENDARS")
  );

  // For Events: Check EVENTS parent permission or VIEW_EVENTS child
  const hasEventsAccess =
    hasPermission("EVENTS") || hasPermission("VIEW_EVENTS");
  console.log(
    "hasEventsAccess:",
    hasEventsAccess,
    "| EVENTS:",
    hasPermission("EVENTS"),
    "| VIEW_EVENTS:",
    hasPermission("VIEW_EVENTS")
  );
  console.log("=================================");

  // Count how many apps the user has access to
  const accessibleAppsCount = [
    hasWidgetAccess,
    hasCalendarAccess,
    hasEventsAccess,
  ].filter(Boolean).length;

  // If user has no access to any app, show message
  if (accessibleAppsCount === 0) {
    return (
      <React.Fragment>
        <Helmet title="Workspaces" />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            padding: 3,
          }}
        >
          <Typography variant="h5" gutterBottom>
            No Applications Available
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You don't have permission to access any applications. Please contact
            your administrator.
          </Typography>
        </Box>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Helmet title="Workspaces" />
      <HeaderBox>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <AutoAwesomeIcon
                sx={{ fontSize: 40, color: "#fff", opacity: 0.9 }}
              />
              <Box>
                <Typography
                  variant="h3"
                  sx={{ color: "#fff", fontWeight: 700, mb: 1 }}
                >
                  Welcome to Your Workspace
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                >
                  Choose an application to get started with your business
                  operations
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </HeaderBox>

      <StaggeredGrid container spacing={4}>
        {hasWidgetAccess && (
          <Grid item xs={12} sm={12} md={4} lg={4} xl>
            <SpaceAppCards
              amount="Widget"
              percentagecolor={green[500]}
              to="/widget/dashboard"
              description="Engage with website visitors through live chat, co-browsing, and video calls. Track visitor analytics and manage customer interactions in real-time."
            />
          </Grid>
        )}
        {hasCalendarAccess && (
          <Grid item xs={12} sm={12} md={4} lg={4} xl>
            <SpaceAppCards
              amount="Calendar"
              percentagecolor={red[500]}
              to="/calendar/dashboard"
              description="Schedule meetings, manage bookings, and coordinate team availability. Integrate with external calendars and automate your scheduling workflow."
            />
          </Grid>
        )}
        {hasEventsAccess && (
          <Grid item xs={12} sm={12} md={4} lg={4} xl>
            <SpaceAppCards
              amount="Events"
              percentagecolor={green[500]}
              to="/events/dashboard"
              description="Create and manage virtual or hybrid events with custom landing pages, registration forms, speaker management, and attendee tracking."
            />
          </Grid>
        )}
      </StaggeredGrid>
    </React.Fragment>
  );
}

export default SpaceApps;
